// Canvas2D — the 2D half of the split search.
//
// Renders one page of a sheet and draws the search hits on it. Zooms to
// the union of the hits when a query lands, and reports a click on one
// so the other pane can re-query (helpers/selectionBus).
//
// Coordinates: anchors carry `bbox` in PDF POINTS and the page's own
// size in points, while the rendered image is some pixel width. Every
// placement goes points -> image pixels -> screen, and the two scales
// are kept apart on purpose - conflating them is how highlights end up
// plausibly but wrongly positioned, which is worse than not drawing them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Checkbox, Empty, Spin, Tooltip } from "antd";
import { sheetPageObjectUrl } from "api/anchors/anchorApi";
import { url } from "api";
import { useWheelZoom, zoomAbout } from "helpers/wheelZoom";
import VersionChip from "components/Documents/VersionChip";

const HIGHLIGHT = "#fe6919";
const AGREED = "#1a7f37";
// Match the width the ingest pre-renders, so an indexed sheet opens from
// cache. Asking for a width nobody warmed means waiting ~30s for the
// renderer, and the cost is in the page, not the pixels - a smaller
// image would not be meaningfully faster.
const RENDER_WIDTH = 1200;
// The server keeps a raster of each page at these widths and warms the
// first during ingest. Only ever asking for the smallest meant zooming
// in showed a 1200px image blown up - the detail was on the server the
// whole time. Past this much magnification the next size up is fetched
// and swapped in; ptToPx is derived from the image's own natural width,
// so the highlights follow it without any separate bookkeeping.
const RASTER_WIDTHS = [1200, 2400];
const UPGRADE_AT = 1.4;
// Smallest a hit may appear on screen, in CSS pixels.
const MIN_HIT_SCREEN_PX = 12;

const okBox = (b) => Array.isArray(b) && b.length === 4 && b.every(Number.isFinite);
const EMPTY = [];

export default function Canvas2D({
  projectId, sheets = [], hits: allHits = EMPTY, onSelect, selectedId,
  // The text of the question on screen. Only used to decide when an
  // explicit sheet pick stops being sticky - see the effect below.
  query = "",
  // "docId:page" of a hit picked in the results list, so clicking a
  // result opens the page it is actually on.
  focusKey = "",
  // The sheet the canvas is showing, reported up so the page can confine
  // a search to it, and the checkbox that turns that on.
  onActiveSheet, scopeOn = false, onScopeChange,
  // The page raster, handed to the parent so raising an issue can put a
  // picture of the hit on it. A REF, deliberately: reporting the element
  // through a callback would set state on the parent, which re-renders
  // this, which reports again - the React 185 loop the active-sheet
  // report above already had to be rescued from once.
  imgElRef,
  // A box to show on one page whatever the hits are: the glance link's
  // sheetBox, { docId, page (0-based), box: [x0, y0, x1, y1] in PAGE
  // PERCENT, y down }. Page percent needs no page size, so it works on a
  // sheet nothing has been indexed on yet.
  targetBox = null,
  // The pane fills the window: the drawing gets all of it, the sheet list
  // folds away behind a button, and `overlay` (the floating properties
  // panel) sits over the drawing - outside the pan/zoom surface, so
  // dragging or scrolling the panel does not move the sheet.
  fullscreen = false,
  overlay = null,
}) {
  // Only hits on a sheet this pane can open. Building data read from a
  // document that cannot be drawn yet (a DWG) comes back with no docId;
  // counted as a page it made "undefined:undefined" the busiest sheet and
  // blanked the pane.
  const hits = useMemo(() => allHits.filter((h) => h && h.docId != null && h.page != null), [allHits]);
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [imgUrl, setImgUrl] = useState("");
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [rasterW, setRasterW] = useState(RASTER_WIDTHS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // view: scale + translation, in screen pixels over the image.
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef(null);

  // Which page is on screen. A query with hits picks the page carrying
  // most of them; otherwise the first sheet, so the pane is never blank
  // when there is something to show.
  const pageKey = (s) => `${s.docId}:${s.page}`;
  const busiest = useMemo(() => {
    if (!hits.length) return null;
    const tally = new Map();
    for (const h of hits) {
      const k = `${h.docId}:${h.page}`;
      tally.set(k, (tally.get(k) || 0) + 1);
    }
    return [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }, [hits]);

  const [manualKey, setManualKey] = useState("");
  const activeKey = manualKey || busiest || (sheets[0] ? pageKey(sheets[0]) : "");
  const sheet = useMemo(
    () => sheets.find((s) => pageKey(s) === activeKey) || null,
    [sheets, activeKey]
  );

  // A NEW QUESTION that lands on a different page should move the pane
  // there, so an explicit pick is only sticky until the next search.
  //
  // Keyed on the query text, not on `hits`. Keyed on `hits` it dropped
  // the reader's pick every time the results array changed identity,
  // and the commonest cause of that was the pick itself: choosing a
  // sheet with "search inside this sheet" ticked re-runs the search for
  // the new sheet, which returns a new array, which cleared manualKey,
  // which fell back to `busiest || sheets[0]` - so selecting any sheet
  // bounced the pane straight back to the first one. Clicking a hit did
  // the same thing, because that re-queries too.
  //
  // The query is what "the next search" actually means. Re-running the
  // SAME question - a scope toggle, a sheet change - is not a new
  // question and must leave the reader where they are.
  useEffect(() => { setManualKey(""); }, [query]);

  // Clicking a result in the list opens the page that result is on.
  //
  // Without this the list was not a link to anything: picking a hit set
  // the selection and re-queried, but never told this pane where the hit
  // WAS, so the canvas stayed on whichever page `busiest` had chosen and
  // the reader was looking at a different sheet from the one they had
  // just clicked.
  //
  // Declared after the reset above so that when a pick also derives a
  // new query - clicking across from the 3D pane does - the navigation
  // wins and the reset does not strand the reader on the busiest page.
  useEffect(() => { if (focusKey) setManualKey(focusKey); }, [focusKey]);

  // Load the page image. Object URLs are revoked on replace and unmount:
  // a sheet at 2400px is a few hundred KB and leaking one per query adds
  // up fast in a session.
  useEffect(() => {
    let dead = false;
    let created = "";
    if (!sheet) { setImgUrl(""); return undefined; }
    setLoading(true); setError("");
    sheetPageObjectUrl(projectId, sheet.docId, sheet.page, rasterW, sheet.ver || "")
      .then((u) => {
        if (dead) { URL.revokeObjectURL(u); return; }
        created = u;
        setImgUrl(u);
      })
      .catch((e) => !dead && setError(e.message || "Could not render that sheet"))
      .finally(() => !dead && setLoading(false));
    return () => {
      dead = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [projectId, sheet, rasterW]);

  // A new sheet starts back at the cheap raster.
  useEffect(() => { setRasterW(RASTER_WIDTHS[0]); }, [activeKey]);

  // Zoomed in far enough that the pixels show? Fetch the sharper one.
  // Only ever upgrades: dropping back down while someone is reading
  // would blur the page under them for no gain.
  useEffect(() => {
    if (!imgSize.w || !view.k) return;
    const next = RASTER_WIDTHS.find((w) => w > rasterW);
    if (next && view.k > UPGRADE_AT) setRasterW(next);
  }, [view.k, imgSize.w, rasterW]);

  // points -> image pixels
  //
  // A ROTATED PAGE reports its size unrotated. Every sheet split out of a
  // delivered set carries /Rotate 90: pdftotext writes the text boxes in
  // the page as printed (3024 x 2160, landscape) but reports the page as
  // 2160 x 3024, and the raster comes out landscape too. Dividing the
  // image width by the reported width scaled every box by 1.4 and pushed
  // the highlights off the right-hand edge of the drawing - which read as
  // "nothing is highlighted". The side of the page that runs the same way
  // as the image is the one to divide by.
  const ptToPx = useMemo(() => {
    if (!sheet || !sheet.pageWidth || !imgSize.w) return 0;
    const pw = Number(sheet.pageWidth) || 0;
    const ph = Number(sheet.pageHeight) || pw;
    const imgLandscape = imgSize.w >= imgSize.h;
    const pageLandscape = pw >= ph;
    return imgSize.w / (imgLandscape === pageLandscape ? pw : ph);
  }, [sheet, imgSize.w, imgSize.h]);

  const pageHits = useMemo(
    // ...and only those with a box to draw: a hit located on the page but
    // not within it (building data on an unregistered page) used to become
    // a zero box at the top-left corner, and pulled the zoom there.
    () => (sheet ? hits.filter((h) => String(h.docId) === String(sheet.docId) && h.page === sheet.page
      && (okBox(h.bbox) || (Array.isArray(h.tagBoxes) && h.tagBoxes.some((tb) => okBox(tb.b))))) : []),
    [hits, sheet]
  );

  // WHICH box to highlight for a hit.
  //
  // An anchor is a text LINE, because that is the right unit for search:
  // a note mentioning P-101 is one result, not eight. It is the wrong
  // unit for a highlight - a line of general notes runs most of the
  // width of the sheet, so asking for P-101 lit a band of prose with the
  // tag somewhere inside it.
  //
  // Anchors indexed since the ingest change carry `tagBoxes`: where each
  // tag sits within the line. When the query IS a tag, highlight that
  // word. Otherwise (a prose query, or an older anchor with no boxes)
  // fall back to the line, which is what it has always done.
  const queryTags = useMemo(() => {
    const found = String(query || '').toUpperCase().match(/[A-Z]{1,4}[-_ ]?\d{1,5}[A-Z]?/g);
    return new Set((found || []).map((s) => s.replace(/[-_ ]/g, '')));
  }, [query]);

  const boxOf = useCallback((h) => {
    const boxes = Array.isArray(h.tagBoxes) ? h.tagBoxes : null;
    const ok = (b) => Array.isArray(b) && b.length === 4;
    if (boxes && boxes.length) {
      const wanted = boxes.find((tb) => queryTags.has(String(tb.t || '').toUpperCase()));
      if (wanted && ok(wanted.b)) return wanted.b;
      // One tag on the line and nothing to disambiguate: that IS the
      // thing on this line worth pointing at.
      if (boxes.length === 1 && ok(boxes[0].b)) return boxes[0].b;
    }
    return ok(h.bbox) ? h.bbox : [0, 0, 0, 0];
  }, [queryTags]);

  // Fit the whole page in the pane.
  const fit = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !imgSize.w) return;
    const k = Math.min(el.clientWidth / imgSize.w, el.clientHeight / imgSize.h);
    setView({ k, x: (el.clientWidth - imgSize.w * k) / 2, y: (el.clientHeight - imgSize.h * k) / 2 });
  }, [imgSize]);

  // The link's box, when it is on the page on screen: image pixels.
  const targetPx = useMemo(() => {
    if (!targetBox || !sheet || !imgSize.w || !okBox(targetBox.box)) return null;
    if (String(targetBox.docId) !== String(sheet.docId) || Number(targetBox.page) !== Number(sheet.page)) return null;
    const [a, b, c, d] = targetBox.box;
    return [a / 100 * imgSize.w, b / 100 * imgSize.h, c / 100 * imgSize.w, d / 100 * imgSize.h];
  }, [targetBox, sheet, imgSize.w, imgSize.h]);

  const zoomToPx = useCallback(([x0, y0, x1, y1]) => {
    const el = wrapRef.current;
    if (!el) return;
    const pad = 40;
    const k = Math.max(0.05, Math.min(el.clientWidth / (x1 - x0 + pad * 2), el.clientHeight / (y1 - y0 + pad * 2), 4));
    setView({ k, x: el.clientWidth / 2 - ((x0 + x1) / 2) * k, y: el.clientHeight / 2 - ((y0 + y1) / 2) * k });
  }, []);

  // Zoom to the union of this page's hits, with a margin so the reader
  // sees context and not just the matched words.
  const zoomToHits = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !ptToPx || !pageHits.length) return targetPx ? zoomToPx(targetPx) : fit();
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    for (const h of pageHits) {
      // Zoom to the same boxes that get highlighted, or the view frames
      // a band of prose while the highlight sits on one word inside it.
      const b = boxOf(h);
      if (!Array.isArray(b) || b.length !== 4) continue;
      x0 = Math.min(x0, b[0]); y0 = Math.min(y0, b[1]);
      x1 = Math.max(x1, b[2]); y1 = Math.max(y1, b[3]);
    }
    if (!Number.isFinite(x0)) return fit();
    const pad = 40;
    const bw = (x1 - x0) * ptToPx + pad * 2;
    const bh = (y1 - y0) * ptToPx + pad * 2;
    const k = Math.max(0.05, Math.min(el.clientWidth / bw, el.clientHeight / bh, 4));
    const cx = ((x0 + x1) / 2) * ptToPx;
    const cy = ((y0 + y1) / 2) * ptToPx;
    setView({ k, x: el.clientWidth / 2 - cx * k, y: el.clientHeight / 2 - cy * k });
    return undefined;
  }, [pageHits, ptToPx, fit, boxOf, targetPx, zoomToPx]);

  useEffect(() => {
    if (!imgSize.w) return;
    if (pageHits.length) zoomToHits(); else if (targetPx) zoomToPx(targetPx); else fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSize.w, imgSize.h, pageHits.length, activeKey, !!targetPx]);

  // Expanding or restoring the pane changes the space the drawing has:
  // frame it again for the new size, once the layout has settled.
  const refitRef = useRef(() => {});
  refitRef.current = () => { if (pageHits.length) zoomToHits(); else if (targetPx) zoomToPx(targetPx); else fit(); };
  useEffect(() => {
    if (!imgSize.w) return undefined;
    const t = setTimeout(() => refitRef.current(), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  // Wheel zoom lives in helpers/wheelZoom because every canvas needs it
  // and this one had it backwards: a wheel-up divided the scale instead
  // of multiplying it, so looking closer pushed the drawing away. The
  // React onWheel prop also cannot preventDefault - the browser treats
  // it as passive - so the listener is attached natively there.
  useWheelZoom(wrapRef, (factor, point) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setView((v) => zoomAbout(v, factor, point.x - rect.left, point.y - rect.top, 0.05, 12));
  }, []);

  // Pan. The grab point and the view at grab time are both remembered,
  // so a drag is a pure translation and never accumulates drift.
  const onMouseDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, x: view.x, y: view.y, k: view.k };
  };
  const onMouseMove = (e) => {
    const d = drag.current;
    if (!d) return;
    setView({ k: d.k, x: d.x + (e.clientX - d.sx), y: d.y + (e.clientY - d.sy) });
  };
  const endDrag = () => { drag.current = null; };

  // Tell the page which sheet is on screen, so "search inside this
  // sheet" knows what "this sheet" is.
  //
  // Reported ONLY when it actually changes. Handing the parent a fresh
  // object on every run made it set state, which re-rendered this, which
  // ran the effect again - React 185, "maximum update depth exceeded",
  // and a blank page. A value that has not changed is not news.
  const reported = useRef("");
  useEffect(() => {
    if (!onActiveSheet) return;
    const s = sheets.find((x) => pageKey(x) === activeKey);
    const key = s ? `${s.docId}:${s.page}` : "";
    if (key === reported.current) return;
    reported.current = key;
    onActiveSheet(s ? { docId: String(s.docId), page: s.page, sheetNumber: s.sheetNumber, sheetName: s.sheetName } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, sheets]);

  // How many of the current query's hits landed on each sheet, so the
  // list can say where the matches actually are instead of making the
  // reader click through to find out.
  const hitsBySheet = useMemo(() => {
    const t = new Map();
    for (const h of hits) {
      const k = `${h.docId}:${h.page}`;
      t.set(k, (t.get(k) || 0) + 1);
    }
    return t;
  }, [hits]);

  // THE LIST FOLLOWS THE SEARCH. With hits on the sheets, only the sheets
  // carrying them are listed, most hits first; "All sheets" puts the
  // whole set back. Showing all 130 with a small badge on three of them
  // read as the list not filtering at all.
  const [showAllSheets, setShowAllSheets] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  useEffect(() => { setListOpen(false); }, [fullscreen]);
  const showList = !fullscreen || listOpen;
  useEffect(() => { setShowAllSheets(false); }, [query]);
  const filtering = hits.length > 0 && !showAllSheets;
  const listedSheets = useMemo(() => {
    if (!filtering) return sheets;
    return sheets
      .filter((s) => hitsBySheet.has(pageKey(s)))
      .sort((a, b) => (hitsBySheet.get(pageKey(b)) || 0) - (hitsBySheet.get(pageKey(a)) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets, hitsBySheet, filtering]);

  return (
    <div style={{ display: "flex", height: "100%", minWidth: 0 }}>
      {/* EVERY SHEET, DOWN THE SIDE.
          A dropdown made the set feel like one sheet with an obscure way
          to reach the others. A list makes it a set: what is in it, how
          much text each sheet carries, and - once a query has run - where
          the matches actually are, so nobody clicks through eleven
          sheets to find the one with the hits on it. */}
      <div style={{
        width: 208, flexShrink: 0, borderRight: "1px solid #eee",
        display: showList ? "flex" : "none", flexDirection: "column", background: "#fbfbfc",
      }}>
        <div style={{ padding: "7px 9px", borderBottom: "1px solid #eee", fontSize: 11, color: "#6b7280", fontWeight: 600, letterSpacing: 0.3 }}>
          {filtering ? "SHEETS WITH HITS" : "SHEETS"}
          <span style={{ float: "right", fontWeight: 400 }}>
            {filtering ? `${listedSheets.length} of ${sheets.length}` : sheets.length}
          </span>
          {hits.length > 0 && (
            <div style={{ fontWeight: 400, letterSpacing: 0, marginTop: 3 }}>
              <a onClick={() => setShowAllSheets((v) => !v)} style={{ fontSize: 11 }}>
                {showAllSheets ? "Only sheets with hits" : "All sheets"}
              </a>
            </div>
          )}
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!sheets.length && (
            <div style={{ padding: 10, fontSize: 11, color: "#9ca3af" }}>
              Nothing indexed yet.
            </div>
          )}
          {listedSheets.map((s) => {
            const k = pageKey(s);
            const active = k === activeKey;
            const n = hitsBySheet.get(k) || 0;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setManualKey(k)}
                title={`${s.sheetName || "Sheet"}${s.page ? ` — page ${s.page + 1}` : ""} · ${s.anchors} indexed`}
                style={{
                  display: "block", width: "100%", textAlign: "left", border: "none",
                  borderBottom: "1px solid #f1f2f4", padding: "6px 9px", cursor: "pointer",
                  background: active ? "#e8f1fb" : "transparent",
                  borderLeft: `3px solid ${active ? "#1677ff" : "transparent"}`,
                }}
              >
                <div style={{ display: "flex", gap: 7 }}>
                  {/* Lazily loaded: 120 sheets must not become 120
                      full-resolution fetches the moment the pane opens. */}
                  {s.thumb ? (
                    <img
                      src={s.thumb.startsWith("/") ? `${url}${s.thumb}` : s.thumb}
                      alt=""
                      loading="lazy"
                      style={{
                        width: 34, height: 26, objectFit: "cover", flexShrink: 0,
                        border: "1px solid #e5e7eb", borderRadius: 2, background: "#fff",
                      }}
                    />
                  ) : (
                    <div style={{ width: 34, height: 26, flexShrink: 0, border: "1px dashed #e5e7eb", borderRadius: 2 }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                      <span style={{ fontSize: 12, fontWeight: active ? 700 : 600, color: s.sheetNumber ? "#111" : "#9ca3af" }}>
                        {s.sheetNumber || "unnumbered"}
                      </span>
                      <VersionChip docId={s.docId} rev={s.revisionNumber} count={(s.revisionNumber || 0) + 1} />
                      {s.gridMoved && (
                        <Tooltip title={`The building grid changed (v${s.gridMoved.fromVersion} to v${s.gridMoved.toVersion}) and lines this sheet's matches sit on moved${s.gridMoved.ft != null ? ` up to ${s.gridMoved.ft} ft` : ''}: its model matches are worth a check.`}>
                          <span style={{ fontSize: 9, color: "#8A6112", background: "#FFF7E6", borderRadius: 3, padding: "0 4px" }}>grid moved</span>
                        </Tooltip>
                      )}
                      {n > 0 && (
                        <Badge
                          count={n}
                          overflowCount={999}
                          style={{ marginLeft: "auto", backgroundColor: "#1677ff", fontSize: 10 }}
                        />
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280", lineHeight: 1.3, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(s.sheetName || "").replace(/^\S+\s+-\s+/, "") || <span style={{ color: "#c0c4c9" }}>unnamed</span>}
                    </div>
                    <div style={{ fontSize: 9, color: "#9ca3af", marginTop: 1 }}>
                      {[s.discipline, s.anchors ? `${s.anchors} indexed` : ""].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0, flex: 1, position: "relative" }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 8px", borderBottom: "1px solid #eee" }}>
        {fullscreen && (
          <Tooltip title={listOpen ? "Hide the sheet list" : "Show the sheet list"}>
            <a onClick={() => setListOpen((v) => !v)} style={{ fontSize: 12 }}>
              {listOpen ? "Hide sheets" : `Sheets (${filtering ? listedSheets.length : sheets.length})`}
            </a>
          </Tooltip>
        )}
        {fullscreen && sheet && (
          <span style={{ fontSize: 12, fontWeight: 600 }}>{sheet.sheetNumber || ""} {(sheet.sheetName || "").replace(/^\S+\s+-\s+/, "")}</span>
        )}
        <Tooltip title="Search only the sheet on screen. The 3D side is never confined - a sheet is not something the model has.">
          <Checkbox
            checked={scopeOn}
            disabled={!activeKey}
            onChange={(e) => onScopeChange && onScopeChange(e.target.checked)}
            style={{ fontSize: 12 }}
          >
            Search inside this sheet
          </Checkbox>
        </Tooltip>
        <Tooltip title="Fit the whole sheet">
          <a onClick={fit} style={{ fontSize: 12 }}>Fit</a>
        </Tooltip>
        <Tooltip title="Zoom to the matches on this sheet">
          <a onClick={zoomToHits} style={{ fontSize: 12, opacity: pageHits.length ? 1 : 0.4 }}>Zoom to hits</a>
        </Tooltip>
        <span style={{ fontSize: 12, color: "#888" }}>
          {pageHits.length ? `${pageHits.length} here` : "no hits on this page"}
        </span>
      </div>

      <div
        ref={wrapRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        style={{
          position: "relative", flex: 1, overflow: "hidden", background: "#f5f5f5",
          cursor: drag.current ? "grabbing" : "grab",
        }}
      >
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <Spin />
              <div style={{ marginTop: 10, fontSize: 12, color: "#888", maxWidth: 280 }}>
                Opening the sheet. A page that was never rendered before can
                take up to half a minute; after that it is instant.
              </div>
            </div>
          </div>
        )}
        {!sheets.length && !loading && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            <Empty description="No sheets indexed for this project yet" />
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#c62828", fontSize: 13 }}>
            {error}
          </div>
        )}

        {imgUrl && (
          <div style={{
            position: "absolute", transformOrigin: "0 0",
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          }}>
            <img
              ref={(el) => {
                imgRef.current = el;
                if (imgElRef) imgElRef.current = el;
              }}
              src={imgUrl}
              alt="sheet"
              draggable={false}
              onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
              style={{ display: "block", userSelect: "none" }}
            />
            {targetPx && (
              <div
                title="From the project overview"
                style={{
                  position: "absolute", pointerEvents: "none",
                  left: targetPx[0], top: targetPx[1],
                  width: targetPx[2] - targetPx[0], height: targetPx[3] - targetPx[1],
                  border: `${2 / Math.max(view.k, 0.01)}px dashed ${HIGHLIGHT}`,
                  background: `${HIGHLIGHT}14`,
                }}
              />
            )}
            {ptToPx > 0 && pageHits.map((h) => {
              const [x0, y0, x1, y1] = boxOf(h);
              const on = String(h._id) === String(selectedId);
              const color = h.agreed ? AGREED : HIGHLIGHT;
              // A matched word on a 3000pt sheet is about two image
              // pixels wide, which at fit zoom is invisible - the
              // highlights were being drawn correctly and could not be
              // seen. Grow every box to a minimum SCREEN size and keep
              // the border one screen pixel, both by dividing out the
              // current zoom, so a hit is findable at any scale and
              // still lines up with the text when zoomed in.
              // THE BOX IS THE TEXT. Every hit used to be inflated to a
              // minimum SCREEN size, centred - so a two-character tag on
              // a 3000pt sheet got a box three times its width, sitting
              // over its neighbours. It was visible and wrong, which
              // reads as a broken highlight.
              //
              // The rectangle is now exactly the bbox. When that is too
              // small to notice, a ring is drawn around it instead of
              // stretching it: findable and still true.
              const tw = (x1 - x0) * ptToPx;
              const th = (y1 - y0) * ptToPx;
              const cx = ((x0 + x1) / 2) * ptToPx;
              const cy = ((y0 + y1) / 2) * ptToPx;
              const w = tw;
              const ht = th;
              const tiny = Math.max(tw, th) * view.k < MIN_HIT_SCREEN_PX;
              const ringPx = MIN_HIT_SCREEN_PX / Math.max(view.k, 0.01);
              return (
                <div
                  key={h._id}
                  title={h.text}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => onSelect && onSelect(h)}
                  style={{
                    position: "absolute",
                    left: cx - w / 2, top: cy - ht / 2,
                    width: w, height: ht,
                    border: `${(on ? 2.5 : 1.5) / Math.max(view.k, 0.01)}px solid ${color}`,
                    background: on ? `${color}44` : `${color}26`,
                    borderRadius: 2 / Math.max(view.k, 0.01),
                    cursor: "pointer",
                    // A hit too small to see keeps its true size and
                    // wears a ring, drawn outside the box so it never
                    // covers the text it is pointing at.
                    boxShadow: tiny
                      ? `0 0 0 ${Math.max(1, 1 / view.k)}px ${color}, 0 0 0 ${ringPx / 2}px ${color}33`
                      : undefined,
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
      {overlay}
      </div>
    </div>
  );
}
